/**
 * Player against player on the field (docs/spellweave-modes.md §2): the team
 * ring under each wizard, the spawn shield, and the two flags: standing on
 * their plinths, carried over a wizard's shoulder, or lying where they were
 * dropped with the time until they go home. Each flag uses the art
 * library's still when it has landed (`flag-rose`, `flag-lime`) and a drawn
 * pole and pennant until then.
 */

import type { CtfSnap, FlagSnap } from '@shared/index.js';
import type { ViewPlayer } from '../state/viewModel.js';
import { TEAMS, rgba } from '../palette.js';
import { TAU, UNIT, drawGlow, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import { art } from './sprites.js';

/** Pole height of the drawn stand-in flag, world units. */
const POLE_H = 2.1;
/** Height of the top of the plinth each flag stands on at home (the Twin Fonts plate), world units. */
const PLINTH_TOP = 0.125;

/** The team's ring under a wizard's feet (and a brighter one under your own). */
export function drawTeamRing(ctx: Ctx, cam: Camera, p: ViewPlayer, time: number): void {
  if (p.tm === undefined || p.dead) return;
  const team = TEAMS[p.tm];
  const px = cam.sx(p.rx), py = cam.sy(p.ry), u = UNIT;
  ctx.strokeStyle = rgba(team.color, p.isLocal ? 0.95 : 0.75);
  ctx.lineWidth = p.isLocal ? 2.5 : 2;
  ctx.beginPath();
  ctx.ellipse(px, py, 0.62 * u, 0.27 * u, 0, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = rgba(team.color, 0.35);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(px, py, 0.74 * u, 0.33 * u, 0, 0, TAU);
  ctx.stroke();
  if (p.sh) {
    // spawn shield: a soft dome that flickers as it runs out
    const a = 0.25 + 0.1 * Math.sin(time * 9);
    ctx.fillStyle = rgba(team.color, a);
    ctx.beginPath();
    ctx.ellipse(px, py - 0.7 * u, 0.7 * u, 1.0 * u, 0, 0, TAU);
    ctx.fill();
  }
}

/** A flag image (or the stand-in) with its pole base at screen (x, y), scaled and tilted. */
function drawFlagAt(ctx: Ctx, team: 0 | 1, x: number, y: number, scale: number, tilt: number, time: number): void {
  const u = UNIT;
  const im = art.image(team === 0 ? 'flag-rose' : 'flag-lime');
  ctx.save();
  ctx.translate(x, y);
  if (tilt !== 0) ctx.rotate(tilt);
  ctx.scale(scale, scale);
  if (im && im.origin && im.pxPerUnit) {
    const smooth = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(im.img, im.origin[0] * u, im.origin[1] * u, (im.img.width / im.pxPerUnit) * u, (im.img.height / im.pxPerUnit) * u);
    ctx.imageSmoothingEnabled = smooth;
  } else {
    const side = TEAMS[team];
    ctx.strokeStyle = '#5A4126';
    ctx.lineWidth = Math.max(2, 0.08 * u);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -POLE_H * u);
    ctx.stroke();
    const wave = Math.sin(time * 4 + team) * 0.08 * u;
    ctx.fillStyle = side.color;
    ctx.beginPath();
    ctx.moveTo(0, -POLE_H * u);
    ctx.quadraticCurveTo(0.6 * u, -POLE_H * u + 0.1 * u + wave, 1.1 * u, -POLE_H * u + 0.35 * u);
    ctx.quadraticCurveTo(0.6 * u, -POLE_H * u + 0.55 * u - wave, 0, -POLE_H * u + 0.75 * u);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = side.dark;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = '#D9B25A';
    ctx.beginPath();
    ctx.arc(0, -POLE_H * u, 0.07 * u, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * The flags that stand or lie on the ground (home and dropped); the carried
 * ones are drawn with their carrier (drawCarriedFlag).
 */
export function drawGroundFlags(ctx: Ctx, cam: Camera, ctf: CtfSnap | null, time: number): void {
  if (!ctf) return;
  ctf.f.forEach((f: FlagSnap, i) => {
    const team = i as 0 | 1;
    if (f.st === 'carried') return;
    const x = cam.sx(f.x), y = cam.sy(f.y), u = UNIT;
    drawGlow(ctx, TEAMS[team].color, x, y, (f.st === 'home' ? 2.4 : 3) * u);
    if (f.st === 'home') {
      // on the plate's plinth: the pole's foot stands in the socket on its top
      drawFlagAt(ctx, team, x, y - PLINTH_TOP * u, 1, 0, time);
      return;
    }
    // dropped: lying on its side, a ring counting down to its return home
    drawFlagAt(ctx, team, x - 0.6 * u, y - 0.1 * u, 0.9, Math.PI / 2 - 0.15, time);
    const left = Math.max(0, Math.min(1, (f.t ?? 0) / 15));
    ctx.strokeStyle = rgba(TEAMS[team].color, 0.9);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y - 0.2 * u, 0.55 * u, -Math.PI / 2, -Math.PI / 2 + TAU * left);
    ctx.stroke();
  });
}

/** The flag a wizard carries, over its shoulder (drawn after the wizard). */
export function drawCarriedFlag(ctx: Ctx, cam: Camera, p: ViewPlayer, time: number): void {
  if (p.fl === undefined || p.dead) return;
  const dir = Math.cos(p.facing) >= 0 ? 1 : -1;
  const x = cam.sx(p.rx - dir * 0.3), y = cam.sy(p.ry - 0.35);
  drawFlagAt(ctx, p.fl, x, y, 0.75, -dir * 0.25, time);
}
